/* eslint-disable react/jsx-props-no-spreading */
import clsx from 'clsx';
import React, { type PropsWithChildren, type ReactNode } from 'react';

import type {
  TWBackgroundProp,
  TWBorderWidthProp,
  TWWidthProp,
} from '../../types/tailwind';
import { Icon } from '../Icon';

export const ErrorMarker = () => (
  <div className="h-f2 flex items-center">
    <span data-testid="error-marker">
      <Icon type="alert" backgroundColor="orange" primaryColor="white" />
    </span>
  </div>
);

// New composable components following DetailHeader pattern
export const ModalRow = ({ children }: { children: ReactNode }) => (
  <div className={clsx('flex relative w-full bg-canvas')}>{children}</div>
);

export const ModalLabel = ({
  children,
  hasError = false,
  width = 'w-[14ch] max-w-[16ch]',
  background = 'bg-canvas',
  borderWidth = 'border-r-[1px]',
}: {
  children: ReactNode;
  hasError?: boolean;
  width?: string;
  background?: string;
  borderWidth?: string;
}) => (
  <div
    className={clsx(
      'shrink-0',
      background,
      borderWidth,
      'border-canvas',
      'text-main-500',
      width,
      'flex relative'
    )}
  >
    {hasError && (
      <div className={clsx('absolute items-center flex right-0', 'h-f2')}>
        <ErrorMarker />
      </div>
    )}
    {children}
  </div>
);

export const ModalValue = ({
  children,
  border = false,
  height,
  disabled = false,
  overflow = false,
  isLast = false,
}: {
  children: ReactNode;
  border?: boolean;
  height?: string;
  disabled?: boolean;
  overflow?: boolean;
  isLast?: boolean;
}) => (
  <div
    className={clsx(
      'modal-value',
      !border && 'border-canvas',
      border && (height ?? 'h-f2'),
      border && [
        'border-main-300 border-l-[0.5px] border-r-[0.5px] box-border border-t-[0.5px]',
      ],
      border && isLast && 'border-b-[0.5px]',
      disabled ? 'bg-canvas' : 'bg-main-200',
      'w-[inherit] group/modal-field flex-1 min-w-f0 inline-flex items-center',
      !overflow && 'overflow-y-hidden overflow-x-hidden'
    )}
  >
    {children}
  </div>
);

export const ModalSection = ({
  label,
  children,
  title,
  hasError,
}: {
  label?: string;
  children?: ReactNode;
  title?: ReactNode;
  hasError?: boolean;
}) => (
  <ModalRow>
    <ModalLabel width="w-[14ch]" background="bg-canvas" hasError={hasError}>
      {label}
    </ModalLabel>
    <ModalValue>
      <div className="pt-f2 w-full bg-canvas">
        {title && <div className="pb-f1">{title}</div>}
        {children}
      </div>
    </ModalValue>
  </ModalRow>
);

// Legacy components for backward compatibility
type ModalFieldProps<WidthProp, BackgroundProp, BorderWidthProp> =
  PropsWithChildren<{
    label: React.ReactNode;
    hasError?: boolean;
    background?: TWBackgroundProp<BackgroundProp>;
    labelWidth?: TWWidthProp<WidthProp>;
    borderWidth?: TWBorderWidthProp<BorderWidthProp>;
    disabled?: boolean;
    stacked?: boolean;
    overflow?: boolean;
  }>;

export const ModalField = <WidthProp, BackgroundProp, BorderWidthProp>({
  label,
  hasError = false,
  background,
  borderWidth,
  labelWidth = 'w-[30%] max-w-[16ch]' as TWWidthProp<WidthProp>,
  disabled = false,
  stacked = false,
  children,
  overflow = false,
}: ModalFieldProps<WidthProp, BackgroundProp, BorderWidthProp>) => {
  if (stacked) {
    return (
      <div className="flex flex-col">
        <div className="flex items-center gap-x-f1">
          <label className="text-main-500">{label}</label>
        </div>
        <div className="flex">
          {hasError && (
            <div className="absolute left-[1ch]">
              <ErrorMarker />
            </div>
          )}
          {children}
        </div>
      </div>
    );
  }

  return (
    <ModalRow>
      <ModalLabel
        hasError={hasError}
        width={labelWidth as string}
        background={background as string}
        borderWidth={borderWidth as string}
      >
        {label}
      </ModalLabel>
      {children && (
        <ModalValue overflow={overflow} disabled={disabled}>
          {children}
        </ModalValue>
      )}
    </ModalRow>
  );
};

type ModalFieldV2Props = {
  label: React.ReactNode;
  children: React.ReactNode;
  extraClassNames?: string;
  hasError?: boolean;
  'data-testid'?: string;
};

export const ModalFieldV2 = ({
  label,
  children,
  extraClassNames,
  hasError = false,
  'data-testid': dataTestId,
}: ModalFieldV2Props) => (
  <div
    className={clsx(
      'flex flex-row border-[0.5px] border-main-300 border-b-0 last:border-b-[0.5px]',
      extraClassNames
    )}
    data-testid={dataTestId}
  >
    <div className="w-f16 shrink-0 text-main-500 border-r-[0.5px] border-main-300">
      {hasError && (
        <div className={clsx('absolute items-center flex right-0', 'h-f2')}>
          <ErrorMarker />
        </div>
      )}
      {label}
    </div>
    <div className="flex flex-row justify-between text-left group min-w-f0 w-full hover:bg-negative hover:cursor-pointer">
      {children}
    </div>
  </div>
);
